/**
 * Navigator Tuning · Plan detail parameters panel (feature 130): the Ranking weights and
 * Ranking rules sliders, each with its info tooltip and "was" reset to the plan's config.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Info as InfoIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { changesLabel, paramChanges, pts, RULES, WEIGHTS } from './parameters';
import type { RankingConfigParams } from '../types/navigator-tuning.type';

/** Hover/focus tooltip, portalled so opening it never shifts the panel layout. */
function Info({ label, text }: { label: string; text: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`What is ${label}?`}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#56607a] hover:bg-[#f1ede6] data-[state=delayed-open]:bg-bquest-green data-[state=instant-open]:bg-bquest-green data-[state=delayed-open]:text-white data-[state=instant-open]:text-white"
          >
            <InfoIcon aria-hidden="true" className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="w-[260px] rounded-lg bg-[#1b2232] px-3 py-2.5 text-[13px] font-normal leading-normal text-white shadow-[0_6px_20px_rgba(27,34,50,.25)]"
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function Slider(props: {
  id: string;
  label: string;
  info: string;
  value: number;
  baseline: number;
  min: number;
  max: number;
  step: number;
  display: (v: number) => string;
  share?: string;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  const changed = props.value !== props.baseline;
  return (
    <div
      className={`flex flex-col gap-1.5 rounded-[10px] px-3 py-2.5 ${changed ? 'bg-[#fff8f1] shadow-[inset_3px_0_0_#026857]' : 'bg-[#fcfbf8]'}`}
    >
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-1">
          <label htmlFor={props.id} className="text-[13px] font-semibold text-[#1b2232]">
            {props.label}
          </label>
          <Info label={props.label} text={props.info} />
        </span>
        <span className="flex items-center gap-2">
          {props.share && (
            <span className="whitespace-nowrap rounded-full bg-[#e3efec] px-2 py-0.5 text-xs font-bold tabular-nums text-[#014d41]">
              {props.share} of score
            </span>
          )}
          <span className="font-bold tabular-nums text-[#1b2232]">{props.display(props.value)}</span>
          {changed && !props.disabled && (
            <button
              type="button"
              onClick={() => props.onChange(props.baseline)}
              aria-label={`Reset ${props.label}`}
              className="min-h-7 whitespace-nowrap rounded-md border border-[#d9d3c7] bg-white px-2 text-xs text-[#56607a]"
            >
              was {props.display(props.baseline)} · reset
            </button>
          )}
        </span>
      </div>
      <input
        id={props.id}
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(parseFloat(e.target.value))}
        className="w-full accent-bquest-green disabled:opacity-50"
      />
    </div>
  );
}

function Group({ title, changed, children }: { title: string; changed: number; children: ReactNode }) {
  return (
    <section className="border-b border-[#efeae1] last:border-b-0">
      <h3 className="flex min-h-[52px] items-center gap-2 px-[18px] text-sm font-bold text-[#1b2232]">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-bquest-green" />
        {title}
        {changed > 0 && (
          <span className="rounded-full bg-[#fdecdc] px-[7px] py-px text-[11px] font-bold text-[#9a3412]">{changed} changed</span>
        )}
      </h3>
      <div className="flex flex-col gap-3.5 px-[18px] pb-4">{children}</div>
    </section>
  );
}

export function ParametersPanel({
  params,
  baseline,
  onChange,
}: {
  params: RankingConfigParams;
  baseline: RankingConfigParams;
  onChange: (next: RankingConfigParams) => void;
}) {
  const totalWeight = Object.values(params.weights).reduce((a, b) => a + b, 0) || 1;
  const changes = paramChanges(baseline, params);
  const changedIn = (group: string) => changes.filter((c) => c.group === group).length;
  return (
    <aside aria-label="Tuning parameters" className="flex flex-col rounded-[14px] border border-border-subtle bg-white">
      <div className="flex flex-col gap-3 border-b border-border-subtle px-[18px] py-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-[17px] font-bold text-[#1b2232]">Parameters</h2>
          <span
            className={`rounded-full px-2.5 py-1 text-xs font-bold ${changes.length ? 'bg-[#fdecdc] text-[#9a3412]' : 'bg-[#f1ede6] text-[#56607a]'}`}
          >
            {changes.length ? `${changesLabel(changes.length)} in sandbox` : 'No changes'}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/navigator-tuning/review-changes"
            className="flex min-h-11 flex-[1_1_140px] items-center justify-center rounded-lg bg-bquest-green px-3.5 font-semibold text-white hover:bg-[#014d41]"
          >
            Review changes
          </Link>
          <button
            type="button"
            onClick={() => onChange(baseline)}
            className="min-h-11 rounded-lg border border-[#d9d3c7] bg-white px-3.5 font-semibold text-[#1b2232] hover:bg-[#fcfbf8]"
          >
            Reset all
          </button>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-[#56607a]">
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-bquest-green" />
          Live — recalculates instantly
        </span>
        <p className="text-xs text-[#56607a]">Changes are saved to the draft and replayed on this plan. “was” = this plan’s config.</p>
      </div>
      <Group title="Ranking weights" changed={changedIn('Ranking weights')}>
        {WEIGHTS.map((w) => (
          <Slider
            key={w.key}
            id={`p_${w.key}`}
            label={w.label}
            info={w.info}
            value={params.weights[w.key]}
            baseline={baseline.weights[w.key]}
            min={0}
            max={1}
            step={0.01}
            display={pts}
            share={`${Math.round((params.weights[w.key] / totalWeight) * 100)}%`}
            disabled={w.disabled}
            onChange={(v) => onChange({ ...params, weights: { ...params.weights, [w.key]: v } })}
          />
        ))}
      </Group>
      <Group title="Ranking rules" changed={changedIn('Ranking rules')}>
        {RULES.map((r) => (
          <Slider
            key={r.key}
            id={`p_${r.key}`}
            label={r.label}
            info={r.info}
            value={params[r.key]}
            baseline={baseline[r.key]}
            min={r.min}
            max={r.max}
            step={r.step}
            display={r.pts ? pts : String}
            onChange={(v) => onChange({ ...params, [r.key]: v })}
          />
        ))}
      </Group>
    </aside>
  );
}
